import React, { useEffect } from 'react';
import Icon from './AppIcon';

const ConfirmationModal = ({
  isOpen,
  onConfirm,
  onCancel,
  title,
  message,
  consequence,
  confirmLabel = 'Bekreft',
  cancelLabel = 'Avbryt',
  isDanger = true,
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        background: 'rgba(0, 0, 0, 0.65)',
        backdropFilter: 'blur(4px)',
        animation: 'fadeIn 180ms ease-out',
      }}
      onClick={(e) => { if (e?.target === e?.currentTarget) onCancel(); }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-modal-title"
    >
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { transform: translateY(24px) scale(0.97); opacity: 0; } to { transform: translateY(0) scale(1); opacity: 1; } }
      `}</style>
      <div
        style={{
          background: 'var(--color-card)',
          border: '2px solid var(--color-border)',
          borderTop: isDanger ? '3px solid #FF5722' : '3px solid var(--color-primary)',
          borderRadius: 'var(--radius-md)',
          padding: '32px',
          maxWidth: '440px',
          width: '100%',
          position: 'relative',
          animation: 'slideUp 220ms cubic-bezier(0.34, 1.2, 0.64, 1)',
          boxShadow: '0 24px 64px rgba(0,0,0,0.4)',
        }}
      >
        {/* Geometric corner accent */}
        <div style={{ position: 'absolute', top: '-1px', right: '-1px', width: '40px', height: '40px', borderRight: isDanger ? '2px solid rgba(255,87,34,0.4)' : '2px solid rgba(0,79,152,0.3)', borderTop: isDanger ? '2px solid rgba(255,87,34,0.4)' : '2px solid rgba(0,79,152,0.3)', borderRadius: '0 var(--radius-md) 0 0', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: '-1px', left: '-1px', width: '40px', height: '40px', borderLeft: '2px solid rgba(0,79,152,0.15)', borderBottom: '2px solid rgba(0,79,152,0.15)', borderRadius: '0 0 0 var(--radius-md)', pointerEvents: 'none' }} />

        {/* Icon + Title */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', marginBottom: '20px' }}>
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 'var(--radius-sm)',
              background: isDanger ? 'rgba(255,87,34,0.12)' : 'rgba(0,79,152,0.1)',
              border: isDanger ? '2px solid rgba(255,87,34,0.3)' : '2px solid rgba(0,79,152,0.25)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Icon name={isDanger ? 'AlertTriangle' : 'Info'} size={22} color={isDanger ? '#FF5722' : 'var(--color-primary)'} />
          </div>
          <div>
            <h2
              id="confirm-modal-title"
              style={{
                fontFamily: 'var(--font-heading)',
                fontWeight: 700,
                fontSize: '18px',
                letterSpacing: '0.01em',
                color: 'var(--color-foreground)',
                marginBottom: '4px',
                lineHeight: 1.2,
              }}
            >
              {title}
            </h2>
          </div>
        </div>

        {/* Message */}
        <p
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: '14px',
            color: 'var(--color-muted-foreground)',
            lineHeight: 1.65,
            marginBottom: consequence ? '16px' : '28px',
          }}
        >
          {message}
        </p>

        {/* Consequence box */}
        {consequence && (
          <div
            style={{
              background: isDanger ? 'rgba(255,87,34,0.07)' : 'rgba(0,79,152,0.06)',
              border: isDanger ? '1.5px solid rgba(255,87,34,0.25)' : '1.5px solid rgba(0,79,152,0.2)',
              borderRadius: 'var(--radius-sm)',
              padding: '12px 14px',
              marginBottom: '28px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '10px',
            }}
          >
            <Icon name="Info" size={15} color={isDanger ? '#FF5722' : 'var(--color-primary)'} style={{ flexShrink: 0, marginTop: '1px' }} />
            <p
              style={{
                fontFamily: 'var(--font-caption)',
                fontSize: '13px',
                color: isDanger ? 'rgba(255,87,34,0.9)' : 'var(--color-primary)',
                lineHeight: 1.55,
                margin: 0,
              }}
            >
              {consequence}
            </p>
          </div>
        )}

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: '12px', flexDirection: 'row-reverse' }}>
          <button
            onClick={onConfirm}
            style={{
              flex: 1,
              padding: '11px 20px',
              background: isDanger ? '#FF5722' : 'var(--color-primary)',
              border: isDanger ? '2px solid #FF5722' : '2px solid var(--color-primary)',
              borderRadius: 'var(--radius-sm)',
              fontFamily: 'var(--font-heading)',
              fontSize: '13px',
              fontWeight: 700,
              letterSpacing: '0.07em',
              textTransform: 'uppercase',
              color: '#FFFFFF',
              cursor: 'pointer',
              transition: 'all 200ms ease-out',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = isDanger ? '#E64A19' : '#003d7a'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = isDanger ? '#FF5722' : 'var(--color-primary)'; e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            {confirmLabel}
          </button>
          <button
            onClick={onCancel}
            style={{
              flex: 1,
              padding: '11px 20px',
              background: 'transparent',
              border: '2px solid var(--color-border)',
              borderRadius: 'var(--radius-sm)',
              fontFamily: 'var(--font-heading)',
              fontSize: '13px',
              fontWeight: 600,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              color: 'var(--color-foreground)',
              cursor: 'pointer',
              transition: 'all 200ms ease-out',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--color-foreground)'; e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--color-border)'; e.currentTarget.style.background = 'transparent'; }}
          >
            {cancelLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;
